import React, { useContext, useEffect, useMemo, useState } from "react";
import { Table } from "reactstrap";
import ReactPaginate from "react-paginate";
import { ProductContext } from "../../../../store";
import ReportHeader from "../../../../components/Search/ReportHeader";
import DetailSkeleton from "../../../../components/Skeleton/DetailSkeleton";

const CollectionProduct = () => {
  const [pageLoader, setPageLoader] = useState(true);
  const [limit, setLimit] = useState(25);
  const [page, setPage] = useState(0);
  const [expandedCollections, setExpandedCollections] = useState({});

  const { dateRange, setDateRange, currentWebsiteId = 1 } = useContext(ProductContext);

  const paperPlane = useMemo(() => require("./../../../../assets/images/paper-plane-grey.png"), []);

  // Hard-coded mock data for now (replace with API later)
  const collections = useMemo(
    () => [
      {
        collectionId: "c-1001",
        collectionName: "Cookware",
        products: [
          {
            productId: "7269",
            productName: "Staub Cast Iron Essential French Oven in Pink Peony",
            productImage:
              "https://cdn11.bigcommerce.com/s-hccytny0od/products/7269/images/32961/Staub_Cast_Iron_Essential_French_Oven_in_Pink_Peony__41443.1755266029.500.750.jpg?c=2",
            impressions: 2,
            clicks: 0,
            addToCarts: 0,
            conversions: 0,
            revenue: 0,
            atcRate: 0,
            conversionRate: 0,
          },
          {
            productId: "6102",
            productName: "Nonstick Fry Pan (12 inch)",
            productImage: "",
            impressions: 28,
            clicks: 4,
            addToCarts: 2,
            conversions: 1,
            revenue: 129.99,
            atcRate: 7.14,
            conversionRate: 3.57,
          },
        ],
      },
      {
        collectionId: "c-1002",
        collectionName: "Kitchen Tools",
        products: [
          {
            productId: "8811",
            productName: "Stainless Steel Whisk",
            productImage: "",
            impressions: 54,
            clicks: 9,
            addToCarts: 3,
            conversions: 1,
            revenue: 19.99,
            atcRate: 5.56,
            conversionRate: 1.85,
          },
          {
            productId: "9912",
            productName: "Silicone Spatula Set",
            productImage: "",
            impressions: 40,
            clicks: 6,
            addToCarts: 4,
            conversions: 2,
            revenue: 39.98,
            atcRate: 10.0,
            conversionRate: 5.0,
          },
        ],
      },
      {
        collectionId: "c-1003",
        collectionName: "Bakeware",
        products: [
          {
            productId: "7711",
            productName: "Ceramic Baking Dish",
            productImage: "",
            impressions: 0,
            clicks: 0,
            addToCarts: 0,
            conversions: 0,
            revenue: 0,
            atcRate: 0,
            conversionRate: 0,
          },
        ],
      },
    ],
    []
  );

  useEffect(() => {
    // Keep behavior similar to other reports (even though data is local)
    if (currentWebsiteId) setPageLoader(false);
  }, [currentWebsiteId]);

  const safeInt = (value) => (typeof value === "number" && Number.isFinite(value) ? value : Number(value) || 0);

  const formatPercent = (value) => {
    if (typeof value === "number" && Number.isFinite(value)) return `${value.toFixed(2)}%`;
    if (typeof value === "string" && value.trim() !== "") return value.includes("%") ? value : `${value}%`;
    return "0.00%";
  };

  const formatCurrency = (value) => {
    const n = typeof value === "number" ? value : Number(value);
    if (Number.isFinite(n)) {
      return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return "$0.00";
  };

  // CTR%: conversions / impressions
  const getConversionCTR = (impressions, conversions) => {
    const imp = safeInt(impressions);
    const conv = safeInt(conversions);
    return imp > 0 ? `${((conv / imp) * 100).toFixed(2)}%` : "0.00%";
  };

  const getCollectionTotals = (collection) => {
    const products = collection?.products || [];
    return products.reduce(
      (acc, p) => {
        acc.impressions += safeInt(p?.impressions);
        acc.clicks += safeInt(p?.clicks);
        acc.addToCarts += safeInt(p?.addToCarts);
        acc.conversions += safeInt(p?.conversions);
        acc.revenue += typeof p?.revenue === "number" ? p.revenue : Number(p?.revenue) || 0;
        return acc;
      },
      {
        impressions: 0,
        clicks: 0,
        addToCarts: 0,
        conversions: 0,
        revenue: 0,
      }
    );
  };

  const totalPages = Math.max(1, Math.ceil(collections.length / limit));
  const pageCollections = collections.slice(page * limit, page * limit + limit);

  const toggleCollectionExpansion = (collectionId) => {
    setExpandedCollections((prev) => ({ ...prev, [collectionId]: !prev[collectionId] }));
  };

  return (
    <div>
      {pageLoader ? (
        <DetailSkeleton />
      ) : (
        <React.Fragment>
          <ReportHeader range={dateRange} onDateChange={(range) => setDateRange(range)} />
          <div className="tab-content mt-3">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Collection Products Performance</h4>
              <div className="d-flex align-items-center">
                <label htmlFor="limit" className="me-2">
                  Items Per Page:
                </label>
                <select
                  id="limit"
                  className="form-select"
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(0);
                  }}
                  style={{ width: "100px", fontSize: "12px" }}
                >
                  <option value={25}>25</option>
                  <option value={30}>30</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
            </div>

            <Table className={`table ${pageCollections.length > 0 ? "table-striped" : ""} mt-3 custom-bordered-table`}>
              <thead>
                <tr>
                  <th style={{ width: "30px" }}></th>
                  <th>Collection</th>
                  <th>Clicks</th>
                  <th>Add-to-Cart</th>
                  <th>Conversions</th>
                  <th>Revenue</th>
                  <th>CTR %</th>
                  <th>Conversion Rate %</th>
                </tr>
              </thead>
              <tbody>
                {pageCollections.length === 0 ? (
                  <tr>
                    <td colSpan={8}>
                      <div className="emptyPlaceholder">
                        <div>
                          <div>
                            <img src={paperPlane} className="img-fluid" alt="No data found" />
                          </div>
                          <div className="rsInfoMsg disabled">
                            <div className="flex-1">No records found.</div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  pageCollections.map((collection) => {
                    const collectionId = collection?.collectionId;
                    const isExpanded = !!expandedCollections[collectionId];
                    const products = collection?.products || [];
                    const totals = getCollectionTotals(collection);
                    const conversionRate = totals.impressions > 0 ? (totals.conversions / totals.impressions) * 100 : 0;

                    return (
                      <React.Fragment key={collectionId}>
                        <tr style={{ cursor: "pointer" }} onClick={() => toggleCollectionExpansion(collectionId)}>
                          <td>
                            <i
                              className={`fas fa-chevron-${isExpanded ? "down" : "right"}`}
                              style={{ fontSize: "12px", color: "#666", transition: "transform 0.2s" }}
                            />
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "4px 8px",
                                backgroundColor: "#e3f2fd",
                                borderRadius: "4px",
                                fontSize: "13px",
                                fontWeight: 500,
                              }}
                            >
                              {collection?.collectionName || "N/A"}
                            </span>
                          </td>
                          <td>{safeInt(totals.clicks).toLocaleString()}</td>
                          <td>{safeInt(totals.addToCarts).toLocaleString()}</td>
                          <td>{safeInt(totals.conversions).toLocaleString()}</td>
                          <td>{formatCurrency(totals.revenue)}</td>
                          <td>{getConversionCTR(totals.impressions, totals.conversions)}</td>
                          <td>{formatPercent(conversionRate)}</td>
                        </tr>

                        {isExpanded && products.length > 0 && (
                          <tr>
                            <td colSpan={8} style={{ padding: 0, backgroundColor: "#f8f9fa" }}>
                              <div style={{ padding: "15px", marginLeft: "30px" }}>
                                <Table className="table table-sm mb-0" style={{ backgroundColor: "white" }}>
                                  <thead>
                                    <tr>
                                      <th>Product</th>
                                      <th>Impressions</th>
                                      <th>Clicks</th>
                                      <th>Add-to-Cart</th>
                                      <th>Conversions</th>
                                      <th>Revenue</th>
                                      <th>CTR %</th>
                                      <th>ATC Rate %</th>
                                      <th>Conversion Rate %</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {products.map((p, idx) => (
                                      <tr key={p?.productId || `${collectionId}_product_${idx}`}>
                                        <td>
                                          <div className="d-flex align-items-center">
                                            <img
                                              src={p?.productImage || paperPlane}
                                              alt={p?.productName || "Product"}
                                              width="35"
                                              height="35"
                                              style={{ objectFit: "cover", marginRight: "8px", borderRadius: "4px" }}
                                              onError={(e) => {
                                                e.target.src = paperPlane;
                                              }}
                                            />
                                            <span>{p?.productName || "N/A"}</span>
                                          </div>
                                        </td>
                                        <td>{safeInt(p?.impressions).toLocaleString()}</td>
                                        <td>{safeInt(p?.clicks).toLocaleString()}</td>
                                        <td>{safeInt(p?.addToCarts).toLocaleString()}</td>
                                        <td>{safeInt(p?.conversions).toLocaleString()}</td>
                                        <td>{formatCurrency(p?.revenue)}</td>
                                        <td>{getConversionCTR(p?.impressions, p?.conversions)}</td>
                                        <td>{formatPercent(p?.atcRate)}</td>
                                        <td>{formatPercent(p?.conversionRate)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </Table>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>

            {totalPages > 1 && (
              <div className="d-flex justify-content-center mt-5">
                <ReactPaginate
                  nextLabel=">"
                  onPageChange={(event) => setPage(event.selected)}
                  page={page}
                  pageRangeDisplayed={3}
                  marginPagesDisplayed={2}
                  pageCount={totalPages}
                  previousLabel="<"
                  pageClassName="page-item"
                  pageLinkClassName="page-link"
                  previousClassName="page-item"
                  previousLinkClassName="page-link"
                  nextClassName="page-item"
                  nextLinkClassName="page-link"
                  breakLabel="..."
                  breakClassName="page-item"
                  breakLinkClassName="page-link"
                  containerClassName="pagination"
                  activeClassName="active"
                  renderOnZeroPageCount={null}
                  forcePage={page}
                />
              </div>
            )}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

export default CollectionProduct;
